import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MainLayout } from '../components/layouts/MainLayout';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { offboardingAPI, reviewsAPI, applicationsAPI, usersAPI, directionsAPI, logsAPI } from '../lib/firestore';
import { useAuth } from '../contexts/AuthContext';
import { WelcomeModal } from '../components/WelcomeModal';
import { useWelcomeModal } from '../hooks/useWelcomeModal';
import type { DashboardKPIs, AuditLog } from '../types';
import {
  Users,
  Shield,
  AlertTriangle,
  Building2,
  UserCheck,
  FolderKanban,
  TrendingUp,
  TrendingDown,
  Activity,
  CheckCircle2,
  XCircle,
  Clock,
} from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';

interface ExtendedKPIs extends DashboardKPIs {
  uniqueAdmins: number;
  unassignedApplications: number;
  directionsCount: number;
  applicationsByStatus: { active: number; inactive: number; deprecated: number };
  applicationsByAuthType: { sso: number; local: number; ldap: number; other: number };
  twoFACoverage: { enabled: number; disabled: number };
}

export const DashboardPage: React.FC = () => {
  const { user, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [kpis, setKpis] = useState<ExtendedKPIs | null>(null);
  const [recentLogs, setRecentLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);

  // Admin-specific state (always declared, even if not used by superadmin)
  const [myApps, setMyApps] = useState<any[]>([]);
  const [myPendingOffboardings, setMyPendingOffboardings] = useState(0);
  const [myActiveReviews, setMyActiveReviews] = useState(0);
  const [adminDataLoaded, setAdminDataLoaded] = useState(false);

  // Welcome Modal
  const { shouldShow, isReady, dismissModal, openModal } = useWelcomeModal(user?.email || null);

  useEffect(() => {
    if (isSuperAdmin) {
      loadDashboardData();
    } else {
      loadAdminData();
    }
  }, [isSuperAdmin, user]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [offboardings, reviews, applications, users, directions, logs] = await Promise.all([
        offboardingAPI.getAll(),
        reviewsAPI.getAll(),
        applicationsAPI.getAll(),
        usersAPI.getActive(),
        directionsAPI.getAll(),
        logsAPI.getRecent(10),
      ]);

      const activeOffboardings = offboardings.filter(
        (o) => o.status === 'in_progress'
      ).length;

      const pendingReviews = reviews.filter((r) => r.status === 'in_progress')
        .length;

      // Calculate unique admins
      const adminEmails = new Set<string>();
      applications.forEach((app) => {
        app.admins.forEach((email) => adminEmails.add(email));
      });

      // Calculate unassigned applications
      const unassignedApps = applications.filter((app) => app.admins.length === 0).length;

      // Calculate applications by status
      const appsByStatus = {
        active: applications.filter((a) => a.status === 'active').length,
        inactive: applications.filter((a) => a.status === 'inactive').length,
        deprecated: applications.filter((a) => a.status === 'deprecated').length,
      };

      // Calculate applications by auth type
      const appsByAuthType = {
        sso: applications.filter((a) => a.authType === 'sso').length,
        local: applications.filter((a) => a.authType === 'local').length,
        ldap: applications.filter((a) => a.authType === 'ldap').length,
        other: applications.filter((a) => a.authType === 'other').length,
      };

      // Calculate 2FA coverage
      const twoFACoverage = {
        enabled: applications.filter((a) => a.twoFARequired).length,
        disabled: applications.filter((a) => !a.twoFARequired).length,
      };

      setKpis({
        activeOffboardings,
        pendingReviews,
        securityWarningsCount: 0, // Removed - not actively used
        activeUsersCount: users.length,
        totalApplications: applications.length,
        uniqueAdmins: adminEmails.size,
        unassignedApplications: unassignedApps,
        directionsCount: directions.length,
        applicationsByStatus: appsByStatus,
        applicationsByAuthType: appsByAuthType,
        twoFACoverage: twoFACoverage,
        averageOffboardingCompletionTime: 0,
        completionRate: 0,
      });

      setRecentLogs(logs);
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAdminData = async () => {
    if (!user?.email) return;
    const userEmail = user.email;

    try {
      setLoading(true);

      // Get only MY applications
      const allApps = await applicationsAPI.getAll();
      const myApplications = allApps.filter((app) => app.admins.includes(userEmail));
      setMyApps(myApplications);

      // Get pending offboardings for MY apps
      const allOffboardings = await offboardingAPI.getAll();
      let pendingCount = 0;
      allOffboardings.forEach((offboarding) => {
        if (offboarding.status !== 'completed') {
          offboarding.applications.forEach((app) => {
            if (app.adminEmails.includes(userEmail)) {
              const myStatus = app.confirmationStatus[userEmail];
              if (!myStatus || myStatus.status === 'pending') {
                pendingCount++;
              }
            }
          });
        }
      });
      setMyPendingOffboardings(pendingCount);

      // Get active reviews for MY apps
      const allReviews = await reviewsAPI.getAll();

      // Filter out malformed reviews
      const validReviews = allReviews.filter(review =>
        review && Array.isArray(review.applicationIds)
      );

      const myReviews = validReviews.filter(
        (review) =>
          review.status === 'in_progress' &&
          review.applicationIds.some((appId) =>
            myApplications.some((app) => app.id === appId)
          )
      );

      setMyActiveReviews(myReviews.length);

      setAdminDataLoaded(true);
    } catch (error) {
      console.error('Error loading admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <MainLayout onHelpClick={openModal}>
        <div>Loading dashboard...</div>
      </MainLayout>
    );
  }

  // Admin view (non-superadmin)
  if (!isSuperAdmin) {

    return (
      <MainLayout onHelpClick={openModal}>
        <div className="space-y-6">
          {/* Hero Section for Admins */}
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-indigo-600 via-indigo-700 to-indigo-800 p-8 text-white shadow-2xl">
            <div className="absolute inset-0 opacity-10">
              <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <pattern id="admin-grid" width="40" height="40" patternUnits="userSpaceOnUse">
                    <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="1"/>
                  </pattern>
                </defs>
                <rect width="100%" height="100%" fill="url(#admin-grid)" />
              </svg>
            </div>

            <div className="relative z-10">
              <h1 className="text-3xl font-bold mb-2">Welcome, Application Admin</h1>
              <p className="text-indigo-100 text-lg mb-4">
                Manage your {myApps.length} assigned application{myApps.length !== 1 ? 's' : ''}
              </p>
              {myPendingOffboardings > 0 && (
                <div className="bg-orange-500/20 border border-orange-300/30 rounded-lg p-3 mb-4">
                  <p className="text-sm font-medium">
                    ⚠️ You have {myPendingOffboardings} pending confirmation{myPendingOffboardings !== 1 ? 's' : ''}
                  </p>
                </div>
              )}
              <Button
                variant="secondary"
                size="lg"
                onClick={() => navigate('/my-tasks')}
                className="gap-2"
              >
                <Activity className="w-5 h-5" />
                View My Tasks
              </Button>
            </div>

            <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/10 rounded-full blur-3xl"></div>
            <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-purple-400/20 rounded-full blur-3xl"></div>
          </div>

          {/* Quick Stats for Admins - MY DATA */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="border-l-4 border-l-indigo-500">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">My Applications</CardTitle>
                <FolderKanban className="h-4 w-4 text-indigo-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{myApps.length}</div>
                <p className="text-xs text-muted-foreground mt-1">
                  Applications I manage
                </p>
              </CardContent>
            </Card>

            <Card className={`border-l-4 ${myPendingOffboardings > 0 ? 'border-l-orange-500 bg-orange-50 dark:bg-orange-900/30' : 'border-l-green-500'}`}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Pending Confirmations</CardTitle>
                <AlertTriangle className={`h-4 w-4 ${myPendingOffboardings > 0 ? 'text-orange-500' : 'text-green-500'}`} />
              </CardHeader>
              <CardContent>
                <div className={`text-2xl font-bold ${myPendingOffboardings > 0 ? 'text-orange-600 dark:text-orange-400' : 'text-green-600'}`}>
                  {myPendingOffboardings}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Offboarding actions needed
                </p>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-purple-500">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Active Reviews</CardTitle>
                <Clock className="h-4 w-4 text-purple-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{myActiveReviews}</div>
                <p className="text-xs text-muted-foreground mt-1">
                  Involving my apps
                </p>
              </CardContent>
            </Card>
          </div>

          {/* My Applications List */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FolderKanban className="h-5 w-5 text-indigo-500" />
                My Applications ({myApps.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {myApps.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <p className="font-medium">No applications assigned</p>
                  <p className="text-sm">Contact your administrator to get assigned to applications</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {myApps.map((app) => (
                    <div
                      key={app.id}
                      className="border rounded-lg p-4 hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="font-semibold">{app.name}</h3>
                        <Badge
                          variant="outline"
                          className={
                            app.status === 'active'
                              ? 'text-green-600 border-green-600'
                              : 'text-gray-600 dark:text-gray-400 border-gray-600'
                          }
                        >
                          {app.status}
                        </Badge>
                      </div>
                      <div className="space-y-1 text-sm">
                        <p className="text-muted-foreground">
                          <strong>Direction:</strong> {app.direction}
                        </p>
                        <p className="text-muted-foreground">
                          <strong>Auth:</strong> {app.authType.toUpperCase()}
                        </p>
                        <p className="text-muted-foreground">
                          <strong>2FA:</strong>{' '}
                          {app.twoFARequired ? (
                            <span className="text-green-600">Enabled</span>
                          ) : (
                            <span className="text-red-600 dark:text-red-400">Disabled</span>
                          )}
                        </p>
                        {app.internetAccessible && (
                          <Badge variant="outline" className="mt-2 text-xs">
                            Internet Accessible
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Call to Action */}
          <Card className="bg-gradient-to-br from-indigo-50 to-indigo-100 dark:from-indigo-900/20 dark:to-indigo-900/30 border-indigo-200 dark:border-indigo-800">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                Your Responsibilities
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                As an application administrator, you are responsible for:
              </p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  Confirming user deactivations for your applications
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  Monitoring access reviews involving your applications
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  Maintaining accurate application information
                </li>
              </ul>
              {myPendingOffboardings > 0 && (
                <div className="mt-6">
                  <Button onClick={() => navigate('/my-tasks')} className="w-full" variant="default">
                    Review {myPendingOffboardings} Pending Action{myPendingOffboardings !== 1 ? 's' : ''}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </MainLayout>
    );
  }

  // SuperAdmin view - Full dashboard
  const statusChartData = [
    { name: 'Active', value: kpis?.applicationsByStatus.active || 0, color: '#10b981' },
    { name: 'Inactive', value: kpis?.applicationsByStatus.inactive || 0, color: '#6b7280' },
    { name: 'Deprecated', value: kpis?.applicationsByStatus.deprecated || 0, color: '#f97316' },
  ];

  const authTypeChartData = [
    { name: 'SSO', value: kpis?.applicationsByAuthType.sso || 0, color: '#3b82f6' },
    { name: 'Local', value: kpis?.applicationsByAuthType.local || 0, color: '#8b5cf6' },
    { name: 'LDAP', value: kpis?.applicationsByAuthType.ldap || 0, color: '#10b981' },
    { name: 'Other', value: kpis?.applicationsByAuthType.other || 0, color: '#f59e0b' },
  ];

  const twoFAChartData = [
    { name: '2FA Enabled', value: kpis?.twoFACoverage.enabled || 0, color: '#10b981' },
    { name: '2FA Disabled', value: kpis?.twoFACoverage.disabled || 0, color: '#ef4444' },
  ];

  return (
    <MainLayout onHelpClick={openModal}>
      <div className="space-y-6">
        {/* Hero Section */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-indigo-600 via-indigo-700 to-indigo-800 p-8 text-white shadow-2xl">
          {/* Background pattern */}
          <div className="absolute inset-0 opacity-10">
            <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <pattern id="dashboard-grid" width="40" height="40" patternUnits="userSpaceOnUse">
                  <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="1"/>
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#dashboard-grid)" />
            </svg>
          </div>

          {/* Content */}
          <div className="relative z-10">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold mb-2">Welcome to Access Review Manager</h1>
                <p className="text-indigo-100 text-base md:text-lg">
                  Monitor and manage your organization's access control system
                </p>
              </div>
              <div className="flex gap-3">
                <div className="bg-white/20 backdrop-blur-sm rounded-lg p-3 md:p-4 text-center flex-1 md:min-w-[140px]">
                  <div className="text-xl md:text-2xl font-bold">{kpis?.activeOffboardings || 0}</div>
                  <div className="text-xs text-indigo-100">Active Offboardings</div>
                </div>
                <div className="bg-white/20 backdrop-blur-sm rounded-lg p-3 md:p-4 text-center flex-1 md:min-w-[140px]">
                  <div className="text-xl md:text-2xl font-bold">{kpis?.pendingReviews || 0}</div>
                  <div className="text-xs text-indigo-100">Pending Reviews</div>
                </div>
              </div>
            </div>
          </div>

          {/* Decorative circles */}
          <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/10 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-purple-400/20 rounded-full blur-3xl"></div>
        </div>

        {/* Main KPI Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Total Applications */}
          <Card className="border-l-4 border-l-indigo-500">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Applications</CardTitle>
              <FolderKanban className="h-4 w-4 text-indigo-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{kpis?.totalApplications || 0}</div>
              <p className="text-xs text-muted-foreground mt-1">
                {kpis?.applicationsByStatus.active || 0} active
              </p>
            </CardContent>
          </Card>

          {/* Unique Admins */}
          <Card className="border-l-4 border-l-purple-500">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Unique Admins</CardTitle>
              <Shield className="h-4 w-4 text-purple-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{kpis?.uniqueAdmins || 0}</div>
              <p className="text-xs text-muted-foreground mt-1">
                Managing {kpis?.totalApplications || 0} apps
              </p>
            </CardContent>
          </Card>

          {/* Active Users */}
          <Card className="border-l-4 border-l-green-500">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Users</CardTitle>
              <UserCheck className="h-4 w-4 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{kpis?.activeUsersCount || 0}</div>
              <p className="text-xs text-muted-foreground mt-1">
                In directory
              </p>
            </CardContent>
          </Card>

          {/* Directions */}
          <Card className="border-l-4 border-l-orange-500">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Directions</CardTitle>
              <Building2 className="h-4 w-4 text-orange-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{kpis?.directionsCount || 0}</div>
              <p className="text-xs text-muted-foreground mt-1">
                Organizational units
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Secondary KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Unassigned Applications */}
          <Card className={kpis && kpis.unassignedApplications > 0 ? 'bg-yellow-50 dark:bg-yellow-900/30' : ''}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Unassigned Apps</CardTitle>
              <AlertTriangle className={`h-4 w-4 ${kpis && kpis.unassignedApplications > 0 ? 'text-yellow-600' : 'text-gray-400'}`} />
            </CardHeader>
            <CardContent>
              <div className={`text-2xl font-bold ${kpis && kpis.unassignedApplications > 0 ? 'text-yellow-600' : ''}`}>
                {kpis?.unassignedApplications || 0}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Apps without admins
              </p>
            </CardContent>
          </Card>

          {/* Active Offboardings */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Offboardings</CardTitle>
              <Clock className="h-4 w-4 text-indigo-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{kpis?.activeOffboardings || 0}</div>
              <p className="text-xs text-muted-foreground mt-1">
                In progress
              </p>
            </CardContent>
          </Card>

          {/* Pending Reviews */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Pending Reviews</CardTitle>
              <Activity className="h-4 w-4 text-indigo-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{kpis?.pendingReviews || 0}</div>
              <p className="text-xs text-muted-foreground mt-1">
                In progress
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Applications by Status - Pie Chart */}
          <Card>
            <CardHeader>
              <CardTitle>Applications by Status</CardTitle>
            </CardHeader>
            <CardContent className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={statusChartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }: any) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {statusChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Applications by Auth Type - Bar Chart */}
          <Card>
            <CardHeader>
              <CardTitle>Authentication Type Distribution</CardTitle>
            </CardHeader>
            <CardContent className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={authTypeChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                    {authTypeChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* 2FA Coverage - Pie Chart */}
          <Card>
            <CardHeader>
              <CardTitle>Two-Factor Authentication</CardTitle>
            </CardHeader>
            <CardContent className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={twoFAChartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }: any) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {twoFAChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* Recent Activity */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5" />
              Recent Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            {recentLogs.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">
                No recent activity
              </p>
            ) : (
              <div className="space-y-3">
                {recentLogs.map((log) => (
                  <div key={log.id} className="flex items-start gap-3 pb-3 border-b last:border-0">
                    <div className="mt-1">
                      {log.action.includes('create') || log.action.includes('add') ? (
                        <CheckCircle2 className="h-4 w-4 text-green-500" />
                      ) : log.action.includes('delete') || log.action.includes('remove') ? (
                        <XCircle className="h-4 w-4 text-red-500" />
                      ) : (
                        <Activity className="h-4 w-4 text-indigo-500" />
                      )}
                    </div>
                    <div className="flex-1 space-y-1">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium">{log.action}</p>
                        <Badge variant="outline" className="text-xs">
                          {log.resource}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span>{log.userEmail}</span>
                        <span>•</span>
                        <span>{new Date(log.timestamp).toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Welcome Modal */}
      {isReady && (
        <WelcomeModal
          isOpen={shouldShow}
          onClose={dismissModal}
          isSuperAdmin={isSuperAdmin}
        />
      )}
    </MainLayout>
  );
};
